import { useEffect, useMemo, useRef, useState } from "react";
import type { Order } from "~/features/orders/types-order";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { InlineSelect } from "~/shared/components/InlineSelect";
import {
  formatMinutesAsTime,
  getTimeSlotOptions,
  TIME_REQUIRED_STEP_MINUTES,
  getWindowMinutes,
  hhmmToMinutes,
  minutesToHHmm,
  type WindowMinutes,
} from "~/shared/utils/delivery-window";
import {
  WindowChoiceFields,
  getInitialWindowChoice,
  getWindowChoiceMinutes,
  type WindowChoice,
} from "~/features/orders/components/WindowChoiceFields";

export type AutocallOrderEditUpdates = { deliveryDate: string; window: WindowMinutes; timeRequired: string };

type AutocallOrderEditModalProps = Readonly<{
  order: Order;
  saving: boolean;
  onClose: () => void;
  onSave: (updates: AutocallOrderEditUpdates) => void;
  tailTop?: number | null;
}>;

function toDateInput(value?: string): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  try {
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
}

function toTimeInput(value?: string): string {
  const v = (value ?? "").trim();
  if (/^\d{2}:\d{2}$/.test(v)) return v;
  if (/^\d{1}:\d{2}$/.test(v)) return `0${v}`;
  return "";
}

function toYmdLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function AutocallOrderEditModal({
  order,
  saving,
  onClose,
  onSave,
  tailTop,
}: AutocallOrderEditModalProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const { presets, loading } = useDeliveryWindows();

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    setHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const currentWindow = order.last_window ?? order.preferred_window ?? null;
  const [deliveryDate, setDeliveryDate] = useState(
    toDateInput(order.requested_delivery_date ?? order.delivery_date) || toDateInput(new Date().toISOString())
  );
  const dateTypingRef = useRef(false);
  const [windowChoice, setWindowChoice] = useState<WindowChoice | null>(() =>
    getInitialWindowChoice(currentWindow, presets)
  );
  // Presets may arrive after the panel opens
  useEffect(() => {
    if (windowChoice === null && presets.length > 0) setWindowChoice(getInitialWindowChoice(currentWindow, presets));
  }, [windowChoice, presets, currentWindow]);
  const windowMinutes = getWindowChoiceMinutes(windowChoice);

  // Time Required: every 30 minutes, 9:00 AM - 8:00 PM, independent of the window.
  // Defaults to the existing value, else the window start; an existing off-list value stays selectable.
  const [timeRequired, setTimeRequired] = useState(() => {
    const existing = toTimeInput(order.time_required);
    if (existing) return existing;
    const minutes = getWindowMinutes(currentWindow);
    return minutesToHHmm(minutes?.startMinutes ?? getTimeSlotOptions(TIME_REQUIRED_STEP_MINUTES)[0].minutes);
  });
  const timeOptions = useMemo(() => {
    const options = getTimeSlotOptions(TIME_REQUIRED_STEP_MINUTES).map(({ value, label }) => ({ value, label }));
    const current = hhmmToMinutes(timeRequired);
    if (current !== null && !options.some((o) => o.value === timeRequired)) {
      options.push({ value: timeRequired, label: formatMinutesAsTime(current).toLowerCase() });
      options.sort((x, y) => (hhmmToMinutes(x.value) ?? 0) - (hhmmToMinutes(y.value) ?? 0));
    }
    return options;
  }, [timeRequired]);

  const saveDisabled = saving || !deliveryDate || !windowMinutes || !timeRequired;
  const minDate = useMemo(() => toYmdLocal(new Date()), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return toYmdLocal(d);
  }, []);
  const rawTop = tailTop ?? 28;
  const tailHeight = 24;
  const inset = 8;
  const topPx = height > 0 ? Math.max(inset, Math.min(height - tailHeight - inset, rawTop)) : rawTop;

  return (
    <div
      ref={rootRef}
      className="modal-panel-enter"
      style={{
        position: "relative",
        background: "white",
        borderRadius: 8,
        border: "1px solid var(--border)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        maxHeight: "calc(100vh - 200px)",
        overflow: "visible",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: topPx,
          left: -12,
          width: 0,
          height: 0,
          borderTop: "12px solid transparent",
          borderBottom: "12px solid transparent",
          borderRight: "12px solid var(--border)",
          zIndex: 2,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: topPx + 1,
          left: -10,
          width: 0,
          height: 0,
          borderTop: "11px solid transparent",
          borderBottom: "11px solid transparent",
          borderRight: "11px solid white",
          zIndex: 3,
        }}
      />
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px 8px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>Edit Order</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {order.sm_number} - {order.patient_name}
          </div>
        </div>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            background: "white",
            border: "1px solid var(--border)",
            borderRadius: "50%",
            width: 28,
            height: 28,
            fontSize: 18,
            cursor: "pointer",
            color: "var(--text-muted)",
            padding: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div style={{ padding: 16, display: "grid", gap: 8, overflow: "auto", flex: 1, minHeight: 0 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Date</span>
            <input
              type="date"
              value={deliveryDate}
              onKeyDown={() => { dateTypingRef.current = true; }}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  if (!dateTypingRef.current) setDeliveryDate('');
                  dateTypingRef.current = false;
                  return;
                }
                dateTypingRef.current = false;
                const parts = v.split('-');
                let sanitized = v;
                if (parts.length === 3) {
                  const month = parts[1] === '00' ? '01' : parts[1];
                  const day = parts[2] === '00' ? '01' : parts[2];
                  sanitized = `${parts[0]}-${month}-${day}`;
                }
                if (sanitized < minDate) { setDeliveryDate(minDate); return; }
                if (sanitized > maxDate) { setDeliveryDate(maxDate); return; }
                setDeliveryDate(sanitized);
              }}
              onBlur={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v < minDate) setDeliveryDate(minDate);
                else if (v > maxDate) setDeliveryDate(maxDate);
              }}
              min={minDate}
              max={maxDate}
              style={{ padding: "10px 12px", fontSize: 14, borderRadius: 6, border: "1px solid var(--border)", outline: "none" }}
            />
          </div>

          <WindowChoiceFields
            value={windowChoice}
            onChange={setWindowChoice}
            presets={presets}
            disabled={saving || loading}
          />

          <div style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Time Required</span>
            <InlineSelect
              value={timeRequired}
              options={timeOptions}
              onChange={setTimeRequired}
              disabled={saving}
              ariaLabel="Time required"
            />
          </div>
      </div>
      <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="primary"
            disabled={saveDisabled}
            onClick={() => {
              if (!windowMinutes) return;
              if (deliveryDate < minDate || deliveryDate > maxDate) {
                alert("Please select only today, tomorrow, or day after tomorrow.");
                return;
              }
              onSave({ deliveryDate, timeRequired, window: windowMinutes });
            }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
      </div>
    </div>
  );
}
